import { Component, OnChanges, OnInit, inject, output, input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { forkJoin, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { KENDO_GRID } from '@progress/kendo-angular-grid';
import { KENDO_DROPDOWNS } from '@progress/kendo-angular-dropdowns';
import { ProductCategory, productCategoryLabel } from 'src/app/common/ProductCategory';
import { vatLabel } from 'src/app/common/vat-label';
import {
  CategoryResolutionObservation,
  ResolutionObservationBase,
  ResolutionOutcomeConstants,
  VatResolutionObservation,
} from 'src/app/common/ResolutionObservation';
import { ResolutionSubjectConstants } from 'src/app/common/ResolutionVocabularyEntry';
import { Customer } from 'src/app/common/Customer';
import { ScanProviderBinding } from 'src/app/common/ScanProviderBinding';
import { Vat } from 'src/app/common/Vat';
import { CustomerService } from 'src/app/services/customer.service';
import { ResolutionObservationsService } from 'src/app/services/resolution-observations.service';
import { ScanProviderBindingService } from 'src/app/services/scan-provider-binding.service';
import { CallApiService } from '../../services/api-action.service';
import { GridNoRecordsComponent } from '../grid/grid-no-records.component';

interface SelectItem {
  value: string;
  label: string;
}

// What a chip click hands the vocabulary form: the term pre-filled, subject and language
// pre-selected from the observation — curation as review instead of transcription.
// Backend #629 follow-up: the source snapshot ("<binding rootUrl> (<customer name>)") rides
// along so the created entry records where the term was observed; null when the observation
// resolves to no known binding (e.g. the GTIN-identify path).
export interface VocabularyPrefill {
  subject: string;
  term: string;
  language: string | null;
  source: string | null;
}

interface ObservationRow {
  id: string;
  createdAt: Date;
  name?: string;
  gtin?: string;
  contentLanguage: string | null;
  languageLabel: string;
  outcome: string;
  outcomeLabel: string;
  // Backend #629 follow-up: the provenance snapshot a chip takeover stamps on the created
  // entry — resolved from the correlation key's binding-id prefix; null when unknown.
  sourceLabel: string | null;
  // The chain's answer: the resolved target, or 'X vs. Y' for the ambiguous tie.
  resultLabel: string;
  // Every step's evidence, as the result cell's tooltip.
  evidenceSummary: string;
  // The prefill chips: source-category segments (+ for VAT the matched term and the name).
  termCandidates: string[];
}

// Backend #623: the observation view beside the vocabulary grid — the FIRST half of the
// curation loop (miss → term → resolved). Newest-first reads of the shadow store, default-
// filtered to the curatable outcomes (Exhausted/Ambiguous; Resolved rows are the chain
// working). One click on a chip lands the term in the vocabulary form with subject and
// language pre-selected. The store is high-volume by design: this view reads the newest
// `take` rows and never tries to be exhaustive.
@Component({
  imports: [CommonModule, KENDO_GRID, KENDO_DROPDOWNS, GridNoRecordsComponent],
  selector: 'app-resolution-observations',
  templateUrl: './resolution-observations.component.html',
  styleUrls: ['./resolution-observations.component.css'],
})
export class ResolutionObservationsComponent implements OnInit, OnChanges {
  private observationsService = inject(ResolutionObservationsService);
  private bindingService = inject(ScanProviderBindingService);
  private customerService = inject(CustomerService);
  private apiActionService = inject(CallApiService);

  // The reference data the page already loads for the vocabulary form — used here to turn
  // target ids back into labels (category shortNames / VAT percentages).
  readonly categories = input<ProductCategory[]>([]);
  readonly vats = input<Vat[]>([]);

  readonly prefill = output<VocabularyPrefill>();

  // The server-side cap — the newest window the view reads.
  public readonly take = 500;

  public readonly subjectItems: SelectItem[] = [
    { value: ResolutionSubjectConstants.ProductCategory, label: $localize`Artikelkategorie` },
    { value: ResolutionSubjectConstants.Vat, label: $localize`MwSt.-Satz` },
  ];

  public readonly allShopsLabel = $localize`Alle Shops`;

  public subject: string = ResolutionSubjectConstants.ProductCategory;
  public shopItems: SelectItem[] = [];
  public selectedBindingId: string | null = null;
  // Default on: Resolved rows are the chain working — the curatable outcomes are the view's
  // purpose.
  public openOnly = true;

  private rows: ObservationRow[] = [];
  private observations: (CategoryResolutionObservation | VatResolutionObservation)[] = [];
  // #629 follow-up: raw reference data behind the provenance snapshot.
  private bindings: ScanProviderBinding[] = [];
  private customers: Customer[] = [];

  public get visibleRows(): ObservationRow[] {
    return this.openOnly
      ? this.rows.filter((r) => r.outcome !== ResolutionOutcomeConstants.Resolved)
      : this.rows;
  }

  public ngOnInit() {
    this.loadShops();
    this.loadObservations();
  }

  public ngOnChanges() {
    // The parent's reference data may arrive after the first observation load — re-derive
    // the display labels from whatever is known now.
    this.rows = this.observations.map((o) => this.toRow(o));
  }

  public onSubjectChange(subject: string) {
    this.subject = subject;
    this.loadObservations();
  }

  public onShopChange(bindingId: string | null) {
    this.selectedBindingId = bindingId;
    this.loadObservations();
  }

  public onToggleOpenOnly() {
    this.openOnly = !this.openOnly;
  }

  public reload() {
    this.loadObservations();
  }

  public onTakeOver(row: ObservationRow, term: string) {
    this.prefill.emit({
      subject: this.subject,
      term,
      language: row.contentLanguage,
      source: row.sourceLabel,
    });
  }

  // The prefill chips. Source categories are raw shop breadcrumbs ('Bücher > Romane') and
  // the resolvers match per segment, so each segment is one candidate term. For the VAT
  // subject the matched vocabulary term (the evidence's leading quoted token — for Ambiguous
  // rows that is the term that tied) comes first, and the product name last: VAT terms are
  // product keywords, and for an Exhausted row the name is what the curator trims down.
  public termCandidates(observation: ResolutionObservationBase, subject: string): string[] {
    const candidates: string[] = [];

    if (subject === ResolutionSubjectConstants.Vat) {
      for (const step of (observation as VatResolutionObservation).steps) {
        const matched = step.evidence?.match(/^"(.+?)"/);
        if (matched) {
          candidates.push(matched[1]);
        }
      }
    }

    candidates.push(
      ...observation.sourceCategories
        .flatMap((s) => s.split('>'))
        .map((segment) => segment.trim())
        .filter((segment) => segment.length > 0)
    );

    if (subject === ResolutionSubjectConstants.Vat && observation.name) {
      candidates.push(observation.name);
    }

    const seen = new Set<string>();
    return candidates.filter((term) => {
      const key = term.toLocaleLowerCase();
      if (seen.has(key)) {
        return false;
      }
      seen.add(key);
      return true;
    });
  }

  private loadShops() {
    // Customers ride along for the provenance snapshot (#629 follow-up): the binding only
    // carries the raw customerId — names are joined client-side, the same pattern as the
    // scan-provider list.
    this.apiActionService.callWithToasts(
      forkJoin({
        bindings: this.bindingService.get(),
        customers: this.customerService.getAllForLookup(),
      }),
      null,
      $localize`Beim Laden der Scan-Provider-Zuordnungen ist ein Fehler aufgetreten.`,
      ({ bindings, customers }) => {
        this.bindings = bindings;
        this.customers = customers;
        this.shopItems = bindings.map((b) => ({ value: b.id, label: b.rootUrl }));
        // Bindings/customers may land after the first observation load — re-derive the rows
        // so their source labels pick the names up.
        this.rows = this.observations.map((o) => this.toRow(o));
      }
    );
  }

  private loadObservations() {
    const prefix = this.selectedBindingId ? `${this.selectedBindingId}:` : undefined;
    const subject = this.subject;

    const observations$: Observable<(CategoryResolutionObservation | VatResolutionObservation)[]> =
      subject === ResolutionSubjectConstants.Vat
        ? this.observationsService.getVatObservations(prefix, this.take)
        : this.observationsService.getCategoryObservations(prefix, this.take);

    this.apiActionService.callWithToasts(
      // A stale response from a filter switched away from must not land in the grid.
      observations$.pipe(map((observations) => ({ observations, subject }))),
      null,
      $localize`Beim Laden der Beobachtungen ist ein Fehler aufgetreten.`,
      ({ observations, subject: loadedSubject }) => {
        if (loadedSubject !== this.subject) {
          return;
        }
        this.observations = observations;
        this.rows = observations.map((o) => this.toRow(o));
      }
    );
  }

  private toRow(observation: CategoryResolutionObservation | VatResolutionObservation): ObservationRow {
    const isVat = this.subject === ResolutionSubjectConstants.Vat;
    return {
      id: observation.id,
      createdAt: new Date(observation.createdAtUtc),
      name: observation.name,
      gtin: observation.gtin,
      contentLanguage: observation.contentLanguage ?? null,
      sourceLabel: this.sourceLabel(observation.correlationKey),
      languageLabel: this.languageLabel(observation.contentLanguage),
      outcome: observation.outcome,
      outcomeLabel: this.outcomeLabel(observation.outcome),
      resultLabel: isVat
        ? this.vatResultLabel(observation as VatResolutionObservation)
        : this.categoryResultLabel(observation as CategoryResolutionObservation),
      evidenceSummary: observation.steps
        .map((s) => `${s.resolverKey}: ${s.evidence ?? '—'}`)
        .join('\n'),
      termCandidates: this.termCandidates(observation, this.subject),
    };
  }

  // Backend #629 follow-up: the provenance snapshot — "<binding rootUrl> (<customer name>)".
  // Scan observations correlate as '<bindingId>:<external key>', so the prefix resolves the
  // shop; a key from another path (e.g. GTIN identify) matches no binding and yields null —
  // the created entry then simply records no source, never a guessed one.
  private sourceLabel(correlationKey: string): string | null {
    const bindingId = correlationKey.split(':')[0];
    const binding = this.bindings.find((b) => b.id === bindingId);
    if (!binding) {
      return null;
    }
    const customerName =
      this.customers.find((c) => c.id === binding.customerId)?.fullName ?? binding.customerId;
    return `${binding.rootUrl} (${customerName})`;
  }

  private vatResultLabel(observation: VatResolutionObservation): string {
    if (observation.resolvedVatId) {
      return this.vatLabel(observation.resolvedVatId);
    }
    const tie = observation.steps.find((s) => s.candidateVatId && s.runnerUpVatId);
    return tie
      ? `${this.vatLabel(tie.candidateVatId)} vs. ${this.vatLabel(tie.runnerUpVatId)}`
      : '—';
  }

  private categoryResultLabel(observation: CategoryResolutionObservation): string {
    // Backend #633 B: a set verdict lists every winner ("Haushalt + Heimtextilien" — many
    // sure matches); the single-winner case reads exactly as before.
    if (observation.resolvedProductCategoryIds?.length) {
      return observation.resolvedProductCategoryIds
        .map((id) => this.categoryLabel(id))
        .join(' + ');
    }
    if (observation.resolvedProductCategoryId) {
      return this.categoryLabel(observation.resolvedProductCategoryId);
    }
    const tie = observation.steps.find(
      (s) => s.candidateProductCategoryId && s.runnerUpProductCategoryId
    );
    return tie
      ? `${this.categoryLabel(tie.candidateProductCategoryId)} vs. ${this.categoryLabel(tie.runnerUpProductCategoryId)}`
      : '—';
  }

  // #178: display only — labelled by the shared rules rather than by the backend's German
  // shortName (VAT) or the ASCII key (category).
  private vatLabel(id: string): string {
    const vat = this.vats().find((v) => v.id === id);
    return vat ? vatLabel(vat) : '?';
  }

  private categoryLabel(id: string): string {
    return productCategoryLabel(this.categories().find((c) => c.id === id)) || '?';
  }

  private languageLabel(language: string | undefined | null): string {
    switch (language) {
      case 'de':
        return $localize`Deutsch`;
      case 'en':
        return $localize`Englisch`;
      default:
        // Undeclared — every vocabulary group ran; homograph ties escalate as Ambiguous.
        return $localize`Nicht festgelegt`;
    }
  }

  private outcomeLabel(outcome: string): string {
    switch (outcome) {
      case ResolutionOutcomeConstants.Resolved:
        return $localize`Ermittelt`;
      case ResolutionOutcomeConstants.Ambiguous:
        return $localize`Mehrdeutig`;
      case ResolutionOutcomeConstants.Exhausted:
        return $localize`Kein Treffer`;
      default:
        return outcome;
    }
  }
}
